import { useQuery, useMutation, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import { salesApi } from '../api/sales';
import { Venta } from '../api/sales';
import { normalizePaginatedResponse, PaginatedResponse } from '../lib/pagination';
import { toast } from 'sonner';

interface UseSalesParams {
  estado?: string;
  cliente?: string;
  fechaInicio?: string;
  fechaFin?: string;
  search?: string;
  page?: number;
  limit?: number;
}

/**
 * Con params de paginación (page/limit) → devuelve PaginatedResponse<Venta>.
 * Sin params de paginación → devuelve Venta[] (compatibilidad).
 */
export function useSales(): {
  data: Venta[] | undefined;
  isLoading: boolean;
  isError: boolean;
  refetch: () => void;
};
export function useSales(params: UseSalesParams & { page: number; limit: number }): {
  data: PaginatedResponse<Venta> | undefined;
  isLoading: boolean;
  isError: boolean;
  refetch: () => void;
};
export function useSales(params?: UseSalesParams) {
  const hasPagination = !!params?.page && !!params?.limit;

  return useQuery({
    queryKey: ['sales', params],
    queryFn: async () => {
      const response = await salesApi.getAll(params);

      if (hasPagination) {
        return normalizePaginatedResponse<Venta>(response);
      }

      return Array.isArray(response?.data) ? response.data : [];
    },
    staleTime: 1000 * 60 * 2,
    placeholderData: keepPreviousData,
  });
}

export function useSale(id: number) {
  return useQuery({
    queryKey: ['sales', id],
    queryFn: () => salesApi.getById(id),
    enabled: !!id && id > 0,
    staleTime: 1000 * 60 * 2,
  });
}

export function useSalesStats() {
  return useQuery({
    queryKey: ['sales', 'stats'],
    queryFn: () => salesApi.getStats(),
    staleTime: 0,
    refetchInterval: 1000 * 5,
  });
}

export function useSalesMensuales(meses: number = 6) {
  return useQuery({
    queryKey: ['sales', 'mensuales', meses],
    queryFn: () => salesApi.getMensuales(meses),
    staleTime: 1000 * 60 * 5,
  });
}

export function useCreateSale() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: any) => salesApi.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['sales'] });
      queryClient.invalidateQueries({ queryKey: ['products'] });
      queryClient.invalidateQueries({ queryKey: ['reports'] });
      toast.success('Venta creada exitosamente');
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.error || 'Error al crear la venta');
    },
  });
}

export function useUpdateSale() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: any }) =>
      salesApi.update(id, data),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: ['sales'] });
      queryClient.invalidateQueries({ queryKey: ['sales', id] });
      queryClient.invalidateQueries({ queryKey: ['sales', 'stats'] });
      queryClient.invalidateQueries({ queryKey: ['products'] });
      queryClient.invalidateQueries({ queryKey: ['reports'] });

      toast.success('Venta actualizada exitosamente');
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.error || 'Error al actualizar la venta');
    },
  });
}

export function useDeleteSale() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => salesApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['sales'] });
      queryClient.invalidateQueries({ queryKey: ['sales', 'stats'] });
      queryClient.invalidateQueries({ queryKey: ['products'] });
      queryClient.invalidateQueries({ queryKey: ['reports'] });

      toast.success('Venta eliminada exitosamente');
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.error || 'Error al eliminar la venta');
    },
  });
}

export function useTopProducts(limit: number = 10, fechaInicio?: string, fechaFin?: string) {
  return useQuery({
    queryKey: ['sales', 'top-products', limit, fechaInicio, fechaFin],
    queryFn: () => salesApi.getTopProducts(limit, fechaInicio, fechaFin),
    staleTime: 0,
    refetchInterval: 1000 * 5,
  });
}

export function useSalesDiarias(dias: number = 7) {
  return useQuery({
    queryKey: ['sales', 'diarias', dias],
    queryFn: () => salesApi.getDiarias(dias),
    staleTime: 1000 * 60 * 5,
  });
}